/**
 * CommittedNumberField (S5, CONSULTS §3) — a committed-edit field for the
 * Edit focus state. Keystrokes register LOCALLY (draft) and style the field
 * as pending within the same event tick (<100ms — signal 1); the value
 * APPLIES only on commit (blur or Enter — signal 4's signified mechanism).
 *
 * The visible commit hint ("Enter to apply · applies when you leave this
 * field") is tied to the pending state — without it the commit-only model
 * is illegible (Norman's invisible-mechanism warning).
 */

import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { GALLERY_STRINGS } from './galleryStrings';

export interface CommittedNumberFieldProps {
  fieldKey: string;
  label: string;
  value: number | undefined;
  min?: number;
  max?: number;
  /** Called ONLY on commit (blur/Enter) with the parsed value (undefined
   *  when the draft is emptied = AI decides). */
  onCommit: (value: number | undefined) => void;
  /** Pending-state reporter — drives the surface's stale indicator. */
  onPendingChange: (fieldKey: string, pending: boolean) => void;
}

export const CommittedNumberField: React.FC<CommittedNumberFieldProps> = ({
  fieldKey,
  label,
  value,
  min,
  max,
  onCommit,
  onPendingChange,
}) => {
  const [draft, setDraft] = useState(value != null ? String(value) : '');
  const [pending, setPending] = useState(false);

  // External value changes (e.g. initial load) reset the draft — the
  // documented adjust-state-during-render pattern (not an effect).
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    setDraft(value != null ? String(value) : '');
    setPending(false);
  }

  const report = (p: boolean) => {
    setPending(p);
    onPendingChange(fieldKey, p);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value); // same-tick pending styling (signal 1, <100ms)
    report(true);
  };

  const commit = () => {
    if (!pending) return;
    const trimmed = draft.trim();
    const parsed = trimmed === '' ? undefined : Number(trimmed);
    onCommit(Number.isFinite(parsed as number) ? parsed : undefined);
    report(false);
  };

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={`committed-${fieldKey}`} className="text-xs">
        {label}
      </Label>
      <Input
        id={`committed-${fieldKey}`}
        data-committed-field={fieldKey}
        data-pending={pending ? 'true' : 'false'}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        className={cn(
          'h-8',
          pending && 'border-coral bg-coral-light',
        )}
      />
      {pending && (
        <span data-commit-hint="true" className="text-[10px] text-coral-dark">
          {GALLERY_STRINGS.editFocus.commitHint}
        </span>
      )}
    </div>
  );
};
